import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Split method definitions with step-by-step guides
export const SPLIT_METHODS = [
  {
    id: 'walk_away',
    name: 'Walk-Away Split',
    description: 'Simple split where bees raise their own queen',
    difficulty: 'Beginner',
    duration: '4-6 weeks',
    color: 'bg-green-500',
    videoUrl: 'https://www.youtube.com/results?search_query=walk+away+split+beekeeping',
    steps: [
      {
        title: 'Assess Parent Hive',
        instructions: 'Ensure the parent hive is strong with at least 8 frames of bees, plenty of brood, and fresh eggs (less than 3 days old).',
        checklist: ['8+ frames of bees', 'Eggs present (less than 3 days old)', 'Good brood pattern', 'No signs of disease'],
        timing: 'Day 1'
      },
      {
        title: 'Prepare Equipment',
        instructions: 'Set up a new hive body with frames. If using foundation, ensure it\'s properly installed.',
        checklist: ['New hive body ready', 'Bottom board', 'Inner cover & outer cover', 'Entrance reducer'],
        timing: 'Day 1'
      },
      {
        title: 'Perform the Split',
        instructions: 'Move 3-4 frames of brood (including eggs) and 2 frames of honey/pollen to the new hive. Include adhering bees. Do NOT move the queen - leave her in the original hive.',
        checklist: ['3-4 brood frames transferred', '2 food frames transferred', 'Queen verified in original hive', 'Shake extra nurse bees into new hive'],
        timing: 'Day 1'
      },
      {
        title: 'Position New Hive',
        instructions: 'Place the new hive at least 3 feet away from the original, or move to a new location entirely. Foragers will return to original location.',
        checklist: ['New hive positioned', 'Entrance reduced', 'Feeder installed if needed'],
        timing: 'Day 1'
      },
      {
        title: 'Initial Check (Optional)',
        instructions: 'After 3-4 days, you can peek to see if emergency queen cells are being built. Do not disturb frames.',
        checklist: ['Queen cells visible', 'Bees calm and working'],
        timing: 'Day 4',
        createReminder: true
      },
      {
        title: 'Queen Cell Check',
        instructions: 'Check for capped queen cells. There should be one or more. Remove excess cells if desired, leaving 1-2 best cells.',
        checklist: ['Capped queen cells present', 'Removed excess cells (optional)'],
        timing: 'Day 10',
        createReminder: true
      },
      {
        title: 'Queen Emergence',
        instructions: 'Queen should emerge around day 16 after the eggs were laid (or day 12-13 after the split if using 3-day eggs). Do not disturb the hive.',
        checklist: ['Leave hive undisturbed', 'Watch for orientation flights'],
        timing: 'Day 14-16',
        createReminder: true
      },
      {
        title: 'Mating Verification',
        instructions: 'Wait 2-3 weeks after queen emergence. Then check for eggs/young larvae which confirms the queen is mated and laying.',
        checklist: ['Eggs present', 'Good laying pattern', 'Queen spotted (optional)'],
        timing: 'Day 35-42',
        createReminder: true
      }
    ]
  },
  {
    id: 'demaree',
    name: 'Demaree Method',
    description: 'Swarm prevention technique that creates a controlled split',
    difficulty: 'Intermediate',
    duration: '3-4 weeks',
    color: 'bg-blue-500',
    videoUrl: 'https://www.youtube.com/results?search_query=demaree+method+beekeeping',
    steps: [
      {
        title: 'Preparation',
        instructions: 'You need: queen excluder, empty super, and a second brood box. Ensure original hive has queen cells or swarm preparations.',
        checklist: ['Queen excluder ready', 'Empty super available', 'Second brood box prepared'],
        timing: 'Day 1'
      },
      {
        title: 'Find the Queen',
        instructions: 'Locate the queen and place her on one frame of brood in the bottom box. Add empty frames or foundation.',
        checklist: ['Queen located', 'Queen on single brood frame in bottom', 'Bottom box filled with empty frames'],
        timing: 'Day 1'
      },
      {
        title: 'Add Queen Excluder & Super',
        instructions: 'Place queen excluder above bottom box. Add a honey super (or empty box) above the excluder.',
        checklist: ['Queen excluder in place', 'Honey super added'],
        timing: 'Day 1'
      },
      {
        title: 'Move Brood Up',
        instructions: 'Place the remaining brood frames in a box on top (above the super). This separates brood from queen.',
        checklist: ['All brood frames in top box', 'Destroy any queen cells in top box'],
        timing: 'Day 1'
      },
      {
        title: 'Weekly Inspection',
        instructions: 'Check top box weekly for queen cells. Destroy any found to prevent swarming. Check bottom for queen activity.',
        checklist: ['Remove queen cells from top', 'Verify queen laying in bottom', 'Add supers if needed'],
        timing: 'Day 7',
        createReminder: true,
        recurring: true
      },
      {
        title: 'Recombine (Optional)',
        instructions: 'After swarm season passes (3-4 weeks), you can recombine by moving brood down, or keep as two-queen system.',
        checklist: ['Decision: recombine or split', 'If splitting: prepare new hive'],
        timing: 'Day 21-28',
        createReminder: true
      }
    ]
  },
  {
    id: 'double_screen',
    name: 'Double Screen Board Split',
    description: 'Keep two colonies in one hive stack with separation',
    difficulty: 'Intermediate',
    duration: '4-6 weeks',
    color: 'bg-purple-500',
    videoUrl: 'https://www.youtube.com/results?search_query=double+screen+board+split',
    steps: [
      {
        title: 'Prepare Double Screen Board',
        instructions: 'A double screen board has two layers of #8 hardware cloth with a gap between them. Bees cannot pass but pheromones can.',
        checklist: ['Double screen board ready', 'Separate entrance for top colony'],
        timing: 'Day 1'
      },
      {
        title: 'Set Up Bottom Colony',
        instructions: 'This will be your queenright colony. Ensure the queen is present with brood.',
        checklist: ['Queen verified in bottom', 'Adequate brood and food'],
        timing: 'Day 1'
      },
      {
        title: 'Add Double Screen',
        instructions: 'Place double screen board on top of the bottom colony. Ensure the upper entrance faces opposite direction.',
        checklist: ['Screen board secured', 'Upper entrance clear and open'],
        timing: 'Day 1'
      },
      {
        title: 'Create Top Colony',
        instructions: 'Add a box with brood frames (including eggs) and food above the screen. Shake in additional nurse bees.',
        checklist: ['Brood with eggs in top', 'Food frames present', 'Nurse bees added'],
        timing: 'Day 1'
      },
      {
        title: 'Check for Queen Cells',
        instructions: 'After 4-5 days, verify queen cells are being built in the top colony.',
        checklist: ['Queen cells started', 'Adequate bee population'],
        timing: 'Day 5',
        createReminder: true
      },
      {
        title: 'Queen Emergence & Mating',
        instructions: 'Allow queen to emerge and mate. The separate entrance allows the new queen to orient.',
        checklist: ['Queen cells hatched', 'Watch for orientation flights'],
        timing: 'Day 16-20',
        createReminder: true
      },
      {
        title: 'Verify Laying Queen',
        instructions: 'Check top colony for eggs confirming successful mating. You can now separate into own hive or keep as double.',
        checklist: ['Eggs in top colony', 'Decision: separate or maintain'],
        timing: 'Day 35-42',
        createReminder: true
      }
    ]
  },
  {
    id: 'nuc_split',
    name: 'Nuc Split',
    description: 'Create a small nucleus colony for backup or sale',
    difficulty: 'Beginner',
    duration: '4-6 weeks',
    color: 'bg-amber-500',
    videoUrl: 'https://www.youtube.com/results?search_query=making+a+nuc+split',
    steps: [
      {
        title: 'Prepare Nuc Box',
        instructions: 'Get a 5-frame nuc box ready with bottom, cover, and entrance.',
        checklist: ['Nuc box assembled', 'Ventilation adequate', 'Feeder ready'],
        timing: 'Day 1'
      },
      {
        title: 'Select Frames',
        instructions: 'From a strong hive, select: 2 frames brood (with eggs), 1 frame honey, 1 frame pollen, 1 empty drawn comb.',
        checklist: ['2 brood frames with eggs', '1 honey frame', '1 pollen frame', '1 empty comb'],
        timing: 'Day 1'
      },
      {
        title: 'Transfer Frames & Bees',
        instructions: 'Move frames to nuc, keeping adhering bees. Shake 1-2 extra frames of nurse bees into nuc. Ensure queen stays in parent hive.',
        checklist: ['Frames transferred', 'Extra bees shaken in', 'Queen not in nuc'],
        timing: 'Day 1'
      },
      {
        title: 'Close and Move',
        instructions: 'Close nuc entrance with screen or grass plug. Move to new location (ideally 2+ miles away) or keep in shade if local.',
        checklist: ['Entrance reduced/closed', 'Moved to final location', 'Feeder installed'],
        timing: 'Day 1'
      },
      {
        title: 'Monitor Queen Cell Development',
        instructions: 'Check after 5-7 days for queen cells. Should see emergency cells being built.',
        checklist: ['Queen cells present', 'Adequate food stores'],
        timing: 'Day 7',
        createReminder: true
      },
      {
        title: 'Verify New Queen',
        instructions: 'After 4-5 weeks total, check for eggs confirming a mated, laying queen.',
        checklist: ['Eggs present', 'Good laying pattern', 'Strong population'],
        timing: 'Day 35',
        createReminder: true
      }
    ]
  },
  {
    id: 'queen_cell',
    name: 'Queen Cell Split',
    description: 'Split using a purchased or raised queen cell',
    difficulty: 'Intermediate',
    duration: '2-3 weeks',
    color: 'bg-pink-500',
    videoUrl: 'https://www.youtube.com/results?search_query=queen+cell+split+beekeeping',
    steps: [
      {
        title: 'Obtain Queen Cell',
        instructions: 'Purchase a capped queen cell from a breeder, or use one from your own queen rearing operation.',
        checklist: ['Queen cell secured', 'Cell capped and viable', 'Know emergence date'],
        timing: 'Day 1'
      },
      {
        title: 'Prepare Split',
        instructions: 'Create a queenless split with brood, food, and bees. Ensure NO other queen cells are present.',
        checklist: ['Queenless split made', 'All emergency cells destroyed', 'Adequate bees and food'],
        timing: 'Day 1'
      },
      {
        title: 'Install Queen Cell',
        instructions: 'Carefully attach queen cell between frames in the brood area. Handle gently - do not rotate or shake.',
        checklist: ['Cell installed properly', 'Protected from crushing', 'In warm brood area'],
        timing: 'Day 1'
      },
      {
        title: 'Queen Emergence',
        instructions: 'Queen should emerge within 1-3 days if cell was near hatching. Do not disturb.',
        checklist: ['Leave hive alone', 'Note expected emergence date'],
        timing: 'Day 2-4',
        createReminder: true
      },
      {
        title: 'Mating Period',
        instructions: 'Allow 1-2 weeks for queen to mature, take mating flights, and begin laying.',
        checklist: ['Good weather for mating flights', 'Drones available in area'],
        timing: 'Day 10-18',
        createReminder: true
      },
      {
        title: 'Verify Laying',
        instructions: 'Check for eggs and young larvae confirming successful mating.',
        checklist: ['Eggs present', 'Queen spotted (optional)', 'Good temperament'],
        timing: 'Day 21',
        createReminder: true
      }
    ]
  },
  {
    id: 'vertical',
    name: 'Vertical Split',
    description: 'Split by separating top and bottom of same hive',
    difficulty: 'Intermediate',
    duration: '4-6 weeks',
    color: 'bg-teal-500',
    videoUrl: 'https://www.youtube.com/results?search_query=vertical+split+beekeeping',
    steps: [
      {
        title: 'Assess Hive Strength',
        instructions: 'Hive should be in two deep boxes with brood in both, or be ready to be reorganized.',
        checklist: ['Strong population', 'Brood available', 'Good food stores'],
        timing: 'Day 1'
      },
      {
        title: 'Locate Queen',
        instructions: 'Find the queen. She will stay in the bottom half (original location).',
        checklist: ['Queen found', 'Mark queen if unmarked'],
        timing: 'Day 1'
      },
      {
        title: 'Prepare Inner Cover',
        instructions: 'Get an inner cover with a notched entrance, or use a bottom board for the top split.',
        checklist: ['Inner cover with entrance ready', 'Or spare bottom board'],
        timing: 'Day 1'
      },
      {
        title: 'Separate Boxes',
        instructions: 'Place inner cover (entrance facing back) on bottom box. Top box goes on top with its own entrance.',
        checklist: ['Inner cover between boxes', 'Top entrance functional', 'Both halves have brood'],
        timing: 'Day 1'
      },
      {
        title: 'Ensure Top Has Eggs',
        instructions: 'The top (queenless) portion must have eggs or very young larvae to raise a new queen.',
        checklist: ['Eggs in top portion', 'Nurse bees present'],
        timing: 'Day 1'
      },
      {
        title: 'Check Queen Cells',
        instructions: 'After 5-7 days, verify queen cells are being built in top portion.',
        checklist: ['Queen cells developing', 'Bottom queen still laying'],
        timing: 'Day 7',
        createReminder: true
      },
      {
        title: 'Separate Into Own Hives',
        instructions: 'Once top has a laying queen (4-5 weeks), move to separate hive body.',
        checklist: ['New queen laying', 'Move to separate equipment', 'Both colonies thriving'],
        timing: 'Day 35-42',
        createReminder: true
      }
    ]
  }
];

// Methods that can stay in the same hive stack to boost the existing colony
export const SAME_HIVE_METHODS = ['demaree', 'double_screen'];
export const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Where a split stands for the Hives list badge/filter: 'steps' (still doing steps) or 'waiting' (waiting on queen result)
export const splitStage = (split) => {
  if (!split || split.outcome || !['in_progress', 'completed'].includes(split.status)) return null;
  const total = methodFor(split.split_method)?.steps.length || 0;
  const done = (split.done_steps || []).length;
  const name = methodLabel(split.split_method, split.split_method_name).replace(/ (Method|Split|Board Split)$/, '');
  if (split.status === 'in_progress' && total && done < total) return { stage: 'steps', label: `${name} · step ${done + 1} of ${total}` };
  return { stage: 'waiting', label: `${name} · waiting on queen` };
};

// Bulk split method values ('walk-away') -> guide ids ('walk_away')
export const methodFor = (id) => SPLIT_METHODS.find(m => m.id === String(id || '').replace(/-/g, '_'));

// Display names for every split method (same as Analytics)
const METHOD_NAMES = { equal_split: 'Equal Split', cutdown: 'Cutdown Split', taranov: 'Taranov Method', custom: 'Custom Split' };
export const methodLabel = (id, fallback) => {
  const key = String(id || '').replace(/-/g, '_');
  return METHOD_NAMES[key] || methodFor(id)?.name || fallback || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Split';
};

// Days from split start for each reminder step (weekly steps repeat 3 times)
export const reminderSchedule = (method) => method.steps.flatMap((step, i) => {
  const m = step.createReminder && step.timing?.match(/Day\s*(\d+)/i);
  if (!m) return [];
  const first = parseInt(m[1]);
  const days = step.recurring ? [first, first * 2, first * 3] : [first];
  return days.map((day, n) => ({ stepIndex: i, step, day, label: days.length > 1 ? ` (week ${n + 1})` : '' }));
});

// Adds the split's step reminders to Tasks & Calendar. Returns how many were created.
export const createSplitReminders = async ({ method, hiveId, hiveNumber, displayNumber, yard, splitId, startDate, headers }) => {
  let created = 0;
  for (const r of reminderSchedule(method)) {
    const d = startDate ? new Date(startDate) : new Date();
    d.setDate(d.getDate() + r.day);
    try {
      await axios.post(`${API}/planned-actions`, {
        hive_id: hiveId, hive_number: hiveNumber ?? null, yard: yard || '', split_id: splitId || null,
        description: `${method.name}: ${r.step.title}${r.label} — ${r.step.instructions}`,
        date: localDate(d), action_type: 'split_step',
        notes: `Step ${r.stepIndex + 1} of ${method.steps.length} for ${method.name} on Hive ${displayNumber || hiveNumber}`,
      }, { headers });
      created += 1;
    } catch { /* keep going if one fails */ }
  }
  return created;
};
